import Alpine from 'alpinejs';
import gsap from 'gsap';

// No custom cursor on touch/mobile devices
const isMobile = window.matchMedia('(hover: none)').matches;

// ─── Dot cursor (global default) ────────────────────────────────────────────
let dotEl = null;

function getDot() {
  if (isMobile) return null;
  if (!dotEl) {
    dotEl = document.createElement('div');
    dotEl.className = 'cursor-dot';
    dotEl.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 1em;
      height: 1em;
      border-radius: 100%;
      background-color: white;
      mix-blend-mode: difference;
      pointer-events: none;
      z-index: 9999;
      will-change: transform;
    `;
    document.body.appendChild(dotEl);

    // Set initial position off-screen, centred via xPercent/yPercent
    gsap.set(dotEl, { xPercent: -50, yPercent: -50, x: -50, y: -50 });

    // Follow mouse — no transition, instant tracking via gsap.set.
    // Also acts as the catch-all reset: if the cursor is anywhere that
    // isn't a [data-cursor-text] element, force the dot/text back into
    // their default states. This is what makes the cursor self-heal
    // when a card is yanked out of the DOM mid-hover (Swup nav, etc.)
    // and the card's own mouseleave never fires.
    document.addEventListener('mousemove', (e) => {
      gsap.set(dotEl, { x: e.clientX, y: e.clientY });
      if (!e.target.closest || !e.target.closest('[data-cursor-text]')) {
        resetToDot();
      }
    });

    // Hide when mouse leaves / enters the window
    document.addEventListener('mouseleave', () => {
      gsap.to(dotEl, { opacity: 0, duration: 0.2 });
    });
    document.addEventListener('mouseenter', () => {
      gsap.to(dotEl, { opacity: 1, duration: 0.2 });
    });

    // Reset on Swup navigation — hide the dot during the transition,
    // restore it (and reset to the default state) once the new page is in.
    if (window.swup) {
      window.swup.hooks.on('visit:start', () => {
        resetToDot();
        gsap.to(dotEl, { opacity: 0, duration: 0.2 });
      });
      window.swup.hooks.on('content:replace', () => {
        // Reset again after content swap, in case any new Alpine init
        // re-triggered the text cursor before mousemove ran.
        resetToDot();
        gsap.to(dotEl, { opacity: 1, duration: 0.2 });
      });
    }
  }
  return dotEl;
}

// Centralised reset — always safe to call. Hides the text cursor and
// snaps the dot back to scale 1. Used by Swup hooks, the global
// mousemove fallback, and Alpine destroy() to defeat the "stuck on
// last card title" bug that happened when a card was removed from
// the DOM mid-hover and never got a chance to fire mouseleave.
//
// Tracks current state so we don't keep firing tweens on every
// mousemove when there's nothing to reset — that was previously
// killing the document-level mouseenter opacity tween (which fires
// when the cursor returns from off-screen) and leaving the dot
// invisible.
let isTextActive = false;
function resetToDot() {
  // Fast path: if neither the text cursor nor the dot is in an
  // overridden state, do nothing. This avoids spamming GSAP with
  // tweens on every mousemove and — crucially — stops us from
  // racing the document-level opacity tween that fires when the
  // mouse re-enters the window.
  if (!isTextActive) return;
  isTextActive = false;
  if (cursorEl) {
    cursorEl.style.opacity = '0';
  }
  if (dotEl) {
    // overwrite: 'auto' only kills other tweens of the same
    // properties (i.e. other scale tweens), so an in-flight opacity
    // tween from document mouseenter/mouseleave is left untouched.
    gsap.to(dotEl, { scale: 1, duration: 0.2, ease: 'power2.out', overwrite: 'auto' });
  }
}

// Initialise dot cursor on first animation frame — guarantees DOM is ready
// and isolates any errors so Alpine is never blocked
requestAnimationFrame(() => {
  try { getDot(); } catch (e) { console.warn('Cursor dot init failed', e); }
});

// ─── Text cursor (project card hover) ───────────────────────────────────────
let cursorEl = null;

function getCursor() {
  if (!cursorEl) {
    cursorEl = document.createElement('div');
    cursorEl.className = 'custom-cursor';
    cursorEl.style.cssText = `
      position: fixed;
      top: -100px;
      left: -100px;
      color: white;
      padding: 0.5rem 0.75rem;
      font-size: 1.65rem;
      mix-blend-mode: difference;
      text-align: center;
      pointer-events: none;
      z-index: 1000;
      opacity: 0;
      transition: opacity 0.2s ease;
      transform: translate(-50%, -50%);
      white-space: nowrap;
    `;
    document.body.appendChild(cursorEl);

    if (window.swup) {
      window.swup.hooks.on('visit:start', () => {
        cursorEl.style.opacity = '0';
      });
    }
  }
  return cursorEl;
}

Alpine.data('cursorFollower', () => ({
  init() {
    if (isMobile) return;
    const cursor = getCursor();
    const dot = getDot();
    const text = this.$el.dataset.cursorText || '';

    this._onEnter = () => {
      cursor.textContent = text;
      cursor.style.opacity = '1';
      isTextActive = true;
      // Shrink dot away — GSAP manages transform so no CSS conflict.
      // overwrite: 'auto' so this only fights other scale tweens, not
      // the document-level opacity tween that runs when the cursor
      // re-enters the window.
      gsap.to(dot, { scale: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' });
    };

    this._onLeave = () => {
      resetToDot();
    };

    this._onMove = (e) => {
      cursor.style.left = e.clientX + 'px';
      cursor.style.top = e.clientY + 'px';
    };

    // Also reset on click — covers the case where a click triggers a
    // Swup nav and the element is removed before mouseleave fires.
    this._onClick = () => {
      resetToDot();
    };

    this.$el.addEventListener('mouseenter', this._onEnter);
    this.$el.addEventListener('mouseleave', this._onLeave);
    this.$el.addEventListener('mousemove', this._onMove);
    this.$el.addEventListener('click', this._onClick);
  },

  // Alpine fires destroy() when the element is removed from the DOM
  // (e.g. when Swup replaces #swup). Reset the cursor state here so
  // even if mouseleave never fired, we don't end up "stuck on the
  // last card title".
  destroy() {
    resetToDot();
  },
}));
